import { useState } from 'react';
import type {
  DocumentGenerationKindDTO,
  DocumentGenerationSpecDTO,
  DocumentGenerationStatusResponseDTO,
} from '@shared/schemas/document-generation';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createDocumentGeneration,
  getDocumentGeneration,
  getGeneratedDocumentVersions,
} from '../services/document-generation.api';

const GENERATION_POLL_BASE_INTERVAL_MS = 2_000;
const GENERATION_POLL_SLOW_INTERVAL_MS = 5_000;
// ~2 min à 2 s avant de ralentir, puis plafond global ~13 min — couvre le budget
// backend (300 s) et ses retries worker avant de cesser de solliciter l'API.
const GENERATION_POLL_SLOWDOWN_AFTER_UPDATES = 60;
const GENERATION_POLL_MAX_UPDATES = 200;
const GENERATION_POLL_MAX_CONSECUTIVE_FAILURES = 5;

export function resolveGenerationPollInterval(input: {
  dataUpdateCount: number;
  fetchFailureCount: number;
  status: DocumentGenerationStatusResponseDTO['status'] | undefined;
}): number | false {
  if (input.status === 'dead' || input.status === 'failed' || input.status === 'succeeded') {
    return false;
  }
  if (input.fetchFailureCount >= GENERATION_POLL_MAX_CONSECUTIVE_FAILURES) {
    return false;
  }
  if (input.dataUpdateCount >= GENERATION_POLL_MAX_UPDATES) {
    return false;
  }
  return input.dataUpdateCount >= GENERATION_POLL_SLOWDOWN_AFTER_UPDATES
    ? GENERATION_POLL_SLOW_INTERVAL_MS
    : GENERATION_POLL_BASE_INTERVAL_MS;
}

export function useDocumentGenerationStatus(conversationId?: string, generationId?: string) {
  return useQuery({
    enabled: Boolean(conversationId && generationId),
    queryFn: () => getDocumentGeneration(conversationId!, generationId!),
    queryKey: ['document-generation', conversationId, generationId],
    refetchInterval: (query) => resolveGenerationPollInterval({
      dataUpdateCount: query.state.dataUpdateCount,
      fetchFailureCount: query.state.fetchFailureCount,
      status: query.state.data?.status,
    }),
  });
}

export function useGeneratedDocumentVersions(input: {
  conversationId?: string;
  documentId?: string;
  enabled: boolean;
  page: number;
  pageSize?: number;
}) {
  const pageSize = input.pageSize ?? 20;
  return useQuery({
    enabled: input.enabled && Boolean(input.conversationId && input.documentId),
    queryFn: () => getGeneratedDocumentVersions(input.conversationId!, input.documentId!, {
      page: input.page,
      pageSize,
    }),
    queryKey: ['generated-document-versions', input.conversationId, input.documentId, input.page, pageSize],
  });
}

export function useDocumentGeneration(conversationId?: string) {
  const [generationId, setGenerationId] = useState<string | null>(null);
  const createMutation = useMutation({
    mutationFn: (input: { kind: DocumentGenerationKindDTO; spec: DocumentGenerationSpecDTO }) => {
      if (!conversationId) {
        throw new Error('Conversation non synchronisée.');
      }
      return createDocumentGeneration({ conversationId, ...input });
    },
    onSuccess: (result) => setGenerationId(result.generationId),
  });
  const statusQuery = useDocumentGenerationStatus(conversationId, generationId ?? undefined);

  return {
    create: createMutation.mutateAsync,
    error: createMutation.error ?? statusQuery.error,
    generationId,
    isPending: createMutation.isPending || statusQuery.data?.status === 'pending' || statusQuery.data?.status === 'generating',
    reset: () => {
      setGenerationId(null);
      createMutation.reset();
    },
    status: statusQuery.data ?? null,
  };
}
